// Pathify
import { make } from 'vuex-pathify'
import UserService from '@/services/user.service'
import EventService from '@/services/event.service'
import { UserTaskType, getEventTaskModifiedAt, getPrimaryEventTask } from '@/store/modules/task'
import { isEventOpen } from '@/store/modules/event'

const ACK_VISIBLE_TTL_MS = 60 * 1000
const SUPPRESS_LOCAL_SIGNAL_TTL_MS = 15 * 1000
const MAX_MISSING_EVENT_VERIFICATIONS_PER_CYCLE = 25
const MISSING_EVENT_VERIFICATION_BATCH_SIZE = 5

let fetchOpenEventsInFlight = null

function getTargetTask(event) {
  return getPrimaryEventTask(event?.tasks)
}

function getTaskCreatorId(task) {
  if (!task) {
    return null
  }
  if (typeof task.created_by === 'number') {
    return task.created_by
  }
  return task.created_by?.id || null
}

function isMyServiceTask(task, userId) {
  return Boolean(
    userId &&
    task?.task_type === UserTaskType.SERVICE &&
    getTaskCreatorId(task) === userId
  )
}

function getEventSegment(event, userId) {
  const targetTask = getTargetTask(event)
  if (!targetTask) {
    return null
  }

  const hasMyAssignment = userId && targetTask.assignees?.some(assignee => assignee.user?.id === userId)
  if (hasMyAssignment || isMyServiceTask(targetTask, userId)) {
    return 'dashboard_my'
  }

  if (!targetTask.closed_at && (!targetTask.assignees || targetTask.assignees.length === 0)) {
    return 'dashboard_unassigned'
  }

  return 'dashboard_other'
}

function cleanupRecentlyAcknowledged(recentlyAcknowledged, now = Date.now()) {
  const cleaned = { ...recentlyAcknowledged }
  for (const [eventId, entry] of Object.entries(cleaned)) {
    if (!entry?.ackedAt || now - entry.ackedAt > ACK_VISIBLE_TTL_MS) {
      delete cleaned[eventId]
    }
  }
  return cleaned
}

function mergeSegmentCollections(baseCollection, closedPending, recentlyAcknowledged, segment) {
  const pending = Object.values(closedPending)
    .filter(entry => entry?.segment === segment && entry?.event)
    .map(entry => entry.event)

  const acknowledged = Object.values(recentlyAcknowledged)
    .filter(entry => entry?.segment === segment && entry?.event)
    .map(entry => entry.event)

  const idsFromSignals = new Set([...pending, ...acknowledged].map(event => String(event.id)))
  const base = (baseCollection || []).filter(event => !idsFromSignals.has(String(event.id)))

  return [...pending, ...acknowledged, ...base]
}

function cleanupLocalSuppression(localSuppression, now = Date.now()) {
  const cleaned = { ...localSuppression }
  for (const [eventId, entry] of Object.entries(cleaned)) {
    if (!entry?.expiresAt || now >= entry.expiresAt) {
      delete cleaned[eventId]
    }
  }
  return cleaned
}

function chunkArray(items, chunkSize) {
  const chunks = []
  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize))
  }
  return chunks
}

const state = {
  // Patrols data
  patrols: {},
  lastFetched: null,

  // Open events data
  openEvents: {
    results: [],
    count: 0,
  },
  openEventsLastFetched: null,

  previousOpenEventMetaById: {},
  changedOpenEventIds: {},
  closedPendingAckByEventId: {},
  recentlyAcknowledgedClosedByEventId: {},
  localSignalSuppressedByEventId: {},

  // Meta states
  loading: false,
  error: null,
}

const mutations = make.mutations(state)

const actions = {
  fetchPatrols({ commit }) {
    commit('loading', true)
    return UserService.fetchDashboard()
      .then(response => {
        commit('patrols', response)
        commit('lastFetched', Date.now())
      })
      .catch(err => {
        commit('error', err?.message || String(err))
        console.error('Error fetching patrols:', err)
      })
      .finally(() => {
        commit('loading', false)
      })
  },

  async fetchOpenEvents({ commit, state, rootState }) {
    if (fetchOpenEventsInFlight) {
      return fetchOpenEventsInFlight
    }

    fetchOpenEventsInFlight = (async () => {
      commit('loading', true)
      const userId = rootState.user.profile?.id
      const now = Date.now()

      const previousMeta = state.previousOpenEventMetaById || {}
      const changedOpenEventIds = { ...state.changedOpenEventIds }
      const closedPendingAckByEventId = { ...state.closedPendingAckByEventId }
      const recentlyAcknowledgedClosedByEventId = cleanupRecentlyAcknowledged(
        state.recentlyAcknowledgedClosedByEventId,
        now
      )
      const localSignalSuppressedByEventId = cleanupLocalSuppression(
        state.localSignalSuppressedByEventId,
        now
      )

      try {
        const response = await EventService.fetchEvents({
          state: 'open'
        })

        if (response?.data) {
          const openResults = response.data.results || []
          const nextMetaById = {}
          const currentOpenIds = new Set()

          openResults.forEach(event => {
            const eventId = String(event.id)
            const segment = getEventSegment(event, userId)
            const lastModifiedAt = getEventTaskModifiedAt(event)
            const previous = previousMeta[eventId]
            const locallySuppressed = Boolean(localSignalSuppressedByEventId[eventId])

            nextMetaById[eventId] = {
              event,
              segment,
              lastModifiedAt,
            }
            currentOpenIds.add(eventId)

            if (
              !locallySuppressed &&
              previous &&
              lastModifiedAt > previous.lastModifiedAt
            ) {
              changedOpenEventIds[eventId] = {
                detectedAt: now,
                lastModifiedAt,
                segment,
              }
            }

            delete closedPendingAckByEventId[eventId]
          })

          const missingEventIds = Object.keys(previousMeta)
            .filter(eventId => !currentOpenIds.has(eventId))
            .filter(eventId => !closedPendingAckByEventId[eventId])
            .filter(eventId => !localSignalSuppressedByEventId[eventId])
            .filter(eventId => !recentlyAcknowledgedClosedByEventId[eventId])
            .slice(0, MAX_MISSING_EVENT_VERIFICATIONS_PER_CYCLE)

          const verificationBatches = chunkArray(
            missingEventIds,
            MISSING_EVENT_VERIFICATION_BATCH_SIZE
          )

          for (const batch of verificationBatches) {
            const results = await Promise.allSettled(
              batch.map(async eventId => {
                const verifyResponse = await EventService.fetch(eventId)
                const event = verifyResponse?.data
                if (!event || isEventOpen(event)) {
                  return null
                }

                const previousSegment =
                  previousMeta[eventId]?.segment ||
                  getEventSegment(previousMeta[eventId]?.event, userId)

                return {
                  eventId,
                  event,
                  segment: previousSegment || getEventSegment(event, userId),
                  lastModifiedAt: getEventTaskModifiedAt(event),
                }
              })
            )

            results.forEach((result, index) => {
              if (result.status === 'rejected') {
                console.warn('Unable to verify event state for id', batch[index], result.reason)
                return
              }

              const data = result.value
              if (!data) {
                return
              }

              closedPendingAckByEventId[data.eventId] = {
                event: data.event,
                segment: data.segment,
                closedDetectedAt: now,
                lastModifiedAt: data.lastModifiedAt,
              }
              delete changedOpenEventIds[data.eventId]
            })
          }

          commit('openEvents', {
            results: openResults,
            count: response.data.count || 0
          })
          commit('previousOpenEventMetaById', nextMetaById)
          commit('changedOpenEventIds', changedOpenEventIds)
          commit('closedPendingAckByEventId', closedPendingAckByEventId)
          commit('recentlyAcknowledgedClosedByEventId', recentlyAcknowledgedClosedByEventId)
          commit('localSignalSuppressedByEventId', localSignalSuppressedByEventId)
          commit('openEventsLastFetched', now)
        }
      } catch (err) {
        commit('error', err?.message || String(err))
        console.error('Error fetching open events:', err)
      } finally {
        commit('loading', false)
      }
    })()

    try {
      return await fetchOpenEventsInFlight
    } finally {
      fetchOpenEventsInFlight = null
    }
  },

  async fetchAll({ dispatch }) {
    await Promise.all([
      dispatch('fetchPatrols'),
      dispatch('fetchOpenEvents')
    ])
  },

  reset({ commit }) {
    commit('patrols', {})
    commit('lastFetched', null)
    commit('openEvents', { results: [], count: 0 })
    commit('openEventsLastFetched', null)
    commit('previousOpenEventMetaById', {})
    commit('changedOpenEventIds', {})
    commit('closedPendingAckByEventId', {})
    commit('recentlyAcknowledgedClosedByEventId', {})
    commit('localSignalSuppressedByEventId', {})
    commit('loading', false)
    commit('error', null)
  },

  suppressLocalEventSignal({ commit, state }, payload) {
    const eventId = payload?.eventId
    if (!eventId) {
      return
    }

    const ttlMs = payload?.ttlMs ?? SUPPRESS_LOCAL_SIGNAL_TTL_MS
    const now = Date.now()
    const localSignalSuppressedByEventId = cleanupLocalSuppression(
      state.localSignalSuppressedByEventId,
      now
    )

    localSignalSuppressedByEventId[String(eventId)] = {
      createdAt: now,
      expiresAt: now + ttlMs,
    }

    commit('localSignalSuppressedByEventId', localSignalSuppressedByEventId)
  },

  markEventAsSeen({ commit, state }, eventId) {
    if (!eventId) {
      return
    }

    const id = String(eventId)
    const changedOpenEventIds = { ...state.changedOpenEventIds }
    const closedPendingAckByEventId = { ...state.closedPendingAckByEventId }
    const recentlyAcknowledgedClosedByEventId = cleanupRecentlyAcknowledged(
      state.recentlyAcknowledgedClosedByEventId,
      Date.now()
    )

    delete changedOpenEventIds[id]

    if (closedPendingAckByEventId[id]) {
      recentlyAcknowledgedClosedByEventId[id] = {
        ...closedPendingAckByEventId[id],
        ackedAt: Date.now(),
      }
      delete closedPendingAckByEventId[id]
    } else if (recentlyAcknowledgedClosedByEventId[id]) {
      recentlyAcknowledgedClosedByEventId[id] = {
        ...recentlyAcknowledgedClosedByEventId[id],
        ackedAt: Date.now(),
      }
    }

    commit('changedOpenEventIds', changedOpenEventIds)
    commit('closedPendingAckByEventId', closedPendingAckByEventId)
    commit('recentlyAcknowledgedClosedByEventId', recentlyAcknowledgedClosedByEventId)
  }
}

const getters = {
  activePatrols: (state) => {
    return state.patrols?.results?.filter(p => p.is_active) || []
  },

  openEventsCount: (state) => state.openEvents.count,

  changedOpenCount: (state) => Object.keys(state.changedOpenEventIds || {}).length,

  closedPendingAckCount: (state) => Object.keys(state.closedPendingAckByEventId || {}).length,

  eventSignalCount: (state, getters) => getters.changedOpenCount + getters.closedPendingAckCount,

  eventSignalCountsBySegment: (state) => {
    const counts = {
      dashboard_my: 0,
      dashboard_unassigned: 0,
      dashboard_other: 0,
    }

    const increase = (entry) => {
      const segment = entry?.segment
      if (segment && counts[segment] !== undefined) {
        counts[segment] += 1
      }
    }

    Object.values(state.changedOpenEventIds || {}).forEach(increase)
    Object.values(state.closedPendingAckByEventId || {}).forEach(increase)

    return counts
  },

  closedPendingAckCountsBySegment: (state) => {
    const counts = {
      dashboard_my: 0,
      dashboard_unassigned: 0,
      dashboard_other: 0,
    }

    Object.values(state.closedPendingAckByEventId || {}).forEach(entry => {
      const segment = entry?.segment
      if (segment && counts[segment] !== undefined) {
        counts[segment] += 1
      }
    })

    return counts
  },

  myEventSignalCount: (state, getters) => getters.eventSignalCountsBySegment.dashboard_my || 0,

  myClosedPendingAckCount: (state, getters) =>
    getters.closedPendingAckCountsBySegment.dashboard_my || 0,

  eventSignalTargetTab: (state, getters) => {
    const counts = getters.eventSignalCountsBySegment
    const priority = ['dashboard_my', 'dashboard_unassigned', 'dashboard_other']
    return priority.find(tabId => (counts[tabId] || 0) > 0) || 'dashboard_my'
  },

  eventSignalById: (state) => {
    const signals = {}

    Object.keys(state.changedOpenEventIds || {}).forEach(eventId => {
      signals[eventId] = {
        ...(signals[eventId] || {}),
        isChanged: true,
      }
    })

    Object.keys(state.closedPendingAckByEventId || {}).forEach(eventId => {
      signals[eventId] = {
        ...(signals[eventId] || {}),
        isClosedPendingAck: true,
      }
    })

    Object.keys(state.recentlyAcknowledgedClosedByEventId || {}).forEach(eventId => {
      signals[eventId] = {
        ...(signals[eventId] || {}),
        isRecentlyAcked: true,
      }
    })

    return signals
  },

  notMyEvents: (state, getters, rootState) => {
    const userId = rootState.user.profile?.id
    if (!userId) return state.openEvents.results.filter(event => {
      const targetTask = getTargetTask(event)
      return targetTask?.assignees?.length > 0
    })

    return state.openEvents.results.filter(event => {
      const targetTask = getTargetTask(event)
      if (!targetTask) return false

      // Event where target task has assignees but not me
      const hasAssignees = targetTask.assignees?.length > 0
      const hasMyAssignment = targetTask.assignees?.some(assignee => assignee.user?.id === userId)
      const isMineAsService = isMyServiceTask(targetTask, userId)

      return hasAssignees && !hasMyAssignment && !isMineAsService
    })
  },

  unassignedEvents: (state, getters, rootState) => {
    const userId = rootState.user.profile?.id
    return state.openEvents.results.filter(event => {
      const targetTask = getTargetTask(event)
      if (!targetTask) return false

      // Check if target task is unassigned and not closed
      if (isMyServiceTask(targetTask, userId)) {
        return false
      }

      return !targetTask.closed_at && (!targetTask.assignees || targetTask.assignees.length === 0)
    })
  },

  // My events - all events where I'm assigned to the dispatcher task or single task
  myEvents: (state, getters, rootState) => {
    const userId = rootState.user.profile?.id
    if (!userId) return []

    return state.openEvents.results.filter(event => {
      const targetTask = getTargetTask(event)
      if (!targetTask) return false

      // Check if I'm assigned to the target task
      return targetTask.assignees?.some(assignee => assignee.user?.id === userId)
        || isMyServiceTask(targetTask, userId)
    })
  },

  myEventsWithSignals: (state, getters) => {
    return mergeSegmentCollections(
      getters.myEvents,
      state.closedPendingAckByEventId,
      cleanupRecentlyAcknowledged(state.recentlyAcknowledgedClosedByEventId),
      'dashboard_my'
    )
  },

  unassignedEventsWithSignals: (state, getters) => {
    return mergeSegmentCollections(
      getters.unassignedEvents,
      state.closedPendingAckByEventId,
      cleanupRecentlyAcknowledged(state.recentlyAcknowledgedClosedByEventId),
      'dashboard_unassigned'
    )
  },

  notMyEventsWithSignals: (state, getters) => {
    return mergeSegmentCollections(
      getters.notMyEvents,
      state.closedPendingAckByEventId,
      cleanupRecentlyAcknowledged(state.recentlyAcknowledgedClosedByEventId),
      'dashboard_other'
    )
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
